import React, { useState, useEffect } from "react";
import "./EditUserPopup.css";
import { User } from "../../../../../../types/User";
import { editUser, getUserMessageTypes, setUserMessageTypes, updateTotals } from "../../../../../../services/api"; // Import the editUser API call and getUserMessageTypes
import { useChat } from "../../../../Layout/ChatProvider/ChatProvider"; // ✅ Import Chat Context
import { CompanyDetails } from "../../../../../../types/CompanyDetails";


// Utility functions for phone number formatting
const formatPhoneNumberForDisplay = (phone: string | undefined): string => {
    if (phone && phone.startsWith("972")) {
        return '0' + phone.slice(3); // Convert 972 prefix to 0
    }
    return phone || "";
};

const formatPhoneNumberForSave = (phone: string | undefined): string => {
    if (phone && phone.startsWith("0")) {
        return "972" + phone.slice(1); // Convert 0 prefix back to 972
    }
    return phone || "";
};

interface EditUserPopupProps {
    user: User;
    userId: string;
    onClose: () => void;
    onConfirm: (userData: {
        firstName: string;
        lastName: string;
        phone: string;
        role: string;
        isOperator: boolean;
        receiveVoice: boolean;
    }) => void;
}

const EditUserPopup: React.FC<EditUserPopupProps> = ({ user, onClose, onConfirm }) => {
    const [firstName, setFirstName] = useState(user.first_name || "");
    const [lastName, setLastName] = useState(user.last_name || "");
    const [phone, setPhone] = useState(formatPhoneNumberForDisplay(user.phone_number?.toString()) || ""); // Format phone for display
    const [role, setRole] = useState(user.job_title_id || "מנהל תזרים");
    const [isOperator, setIsOperator] = useState(user.is_message_contact || false);
    const [receiveVoice, setReceiveVoice] = useState(user.is_voice_messages || false);
    const [isLoading, setIsLoading] = useState(false); // Loading state to handle async requests
    const [error, setError] = useState<string | null>(null); // Error state to display any error message
    const { users, setUsers, companies, setCompanies } = useChat();
    const [isClosing, setIsClosing] = useState(false); // Add closing state
    // Notification/message settings state
    const [notifyNonExpected, setNotifyNonExpected] = useState(false);
    const [notifyNegative, setNotifyNegative] = useState(false);
    const [notifyBudget, setNotifyBudget] = useState(false);
    const [notifyIncomePace, setNotifyIncomePace] = useState(false);
    const [notifyExpensePace, setNotifyExpensePace] = useState(false);
    const [messageTypes, setMessageTypes] = useState<any[]>([]);
    const [ notAdjustedMinTotal, setNotAdjustedMinTotal ] = useState(0);
    const [ nigrarotMinTotal, setNigrarotMinTotal ] = useState(0);
    const [ nigrarotMinDays, setNigrarotMinDays ] = useState(0);
    const [ companyDetails, setCompanyDetails ] = useState<CompanyDetails | null>(null);


    // Populate notification/message settings from getUserMessageTypes
    useEffect(() => {
        const fetchMessageTypes = async () => {
            if (user.id) {
                const types = await getUserMessageTypes(user.id);

                setMessageTypes(types);

                const currentCompany = companies.find((c) => Number(c.id) === Number(user.company_id));
                const companyDetails = currentCompany?.company_details;
                setCompanyDetails(companyDetails || null);

                setNotAdjustedMinTotal(companyDetails?.not_adjusted_min_total ?? 0);
                setNigrarotMinTotal(companyDetails?.nigrarot_min_total ?? 0);
                setNigrarotMinDays(companyDetails?.nigrarot_min_day ?? 0);

                // Updated mapping based on new getUserMessageTypes returned object
                // Assuming each typeObj has: message_type_id, is_active, and possibly threshold/days fields
                types.forEach((typeObj: any) => {
                    console.log("Fetched message types typeObj:", typeObj);
                    switch (typeObj.message_type_id) {
                        case 1:
                            setNotifyNonExpected(!!typeObj.is_active);
                            break;
                        case 2:
                            setNotifyNegative(!!typeObj.is_active);
                            break;

                        case 3:
                            setNotifyBudget(!!typeObj.is_active);
                            break;
                        case 4:
                            setNotifyIncomePace(!!typeObj.is_active);
                            break;
                        case 5:
                            setNotifyExpensePace(!!typeObj.is_active);
                            break;
                        default:
                            break;
                    }
                });
            }
        };
        fetchMessageTypes();

    }, [user.id]);

    const handleSubmit = async () => {
        setIsLoading(true); // Set loading to true when the request is being processed

        try {
            // Convert phone back to international format for saving
            const formattedPhone = formatPhoneNumberForSave(phone);

            // Call the editUser API with the updated user data
            const result = await editUser({
                userId: user.userId || "", // You need to pass the userId for editing
                firstName,
                lastName,
                phone: formattedPhone,
                role,
                isMessageContact: isOperator,
                isVoiceMessage: receiveVoice
            });

            // Check the result from the API call
            if (result.success) {
                // If success, close the popup and do any other action you want
                onConfirm({ firstName, lastName, phone, role, isOperator, receiveVoice });
                onClose();
                setUsers(
                    users.map((u) =>
                        u.userId === user.userId
                            ? {
                                  ...u,
                                  first_name: firstName,
                                  last_name: lastName,
                                  phone_number: Number(formattedPhone),
                                  job_title_id: role,
                                  is_message_contact: isOperator,
                                  is_voice_messages: receiveVoice
                              }
                            : u
                    )
                );
            } else {
                setError(result.message); // Display error if the API call fails
            }

            await handleSetUserMessageTypes();

        // Check if the original values were changed
        if (
            notAdjustedMinTotal !== companyDetails?.not_adjusted_min_total ||
            nigrarotMinTotal !== companyDetails?.nigrarot_min_total ||
            nigrarotMinDays !== companyDetails?.nigrarot_min_day
        ) {
            await updateTotals(
                user.company_id || 0,
                notAdjustedMinTotal,
                nigrarotMinTotal,
                nigrarotMinDays
            );

            // Update only the company_details for this specific company in the companies context
            setCompanies(
                companies.map((company) =>
                    Number(company.id) === Number(user.company_id)
                        ? {
                            ...company,
                            company_details: {
                                ...company.company_details,
                                not_adjusted_min_total: notAdjustedMinTotal,
                                nigrarot_min_total: nigrarotMinTotal,
                                nigrarot_min_day: nigrarotMinDays
                            }
                        }
                        : company
                )
            );
        }

        } catch (error: any) {
            setError(error.message || "An unknown error occurred"); // Handle any unexpected errors
        } finally {
            setIsLoading(false); // Reset loading state after the request is finished
        }
    };

    const handleSetUserMessageTypes = async () => {
        const messageTypesToSave = [
            { message_type_id: 1, is_active: notifyNonExpected },
            { message_type_id: 2, is_active: notifyNegative },
            { message_type_id: 3, is_active: notifyBudget },
            { message_type_id: 4, is_active: notifyIncomePace },
            { message_type_id: 5, is_active: notifyExpensePace }
        ];

        const result = await setUserMessageTypes(user.id, messageTypesToSave);
        console.log(result);
    }

    // Handle the click on the overlay to stop the click event from propagating
    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        // Only close if the user clicks directly on the overlay (not the popup)
        if (e.target === e.currentTarget) {
            handleClose();
        }
    };

    // New close handler to trigger animation
    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            onClose();
        }, 300); // Match the animation duration
    };

    // Helper to check if a message type is enabled
    const isTypeEnabled = (typeName: string) => {
        // typeName is expected to be a string like "LOW_RATE_INCOME", "BUDGET_EXCEEDED", etc.
        // We'll map these to their corresponding message_type_id and search by id.
        const typeNameToId: { [key: string]: number } = {
            "NOT_ADJUSTED": 1,
            "TRAILING": 2,
            "BUDGET_EXCEEDED": 3,
            "LOW_RATE_INCOME": 4,
            "PACE_TOO_FAST": 5
        };
        const typeId = typeNameToId[typeName];
        return messageTypes.some(mt => mt.message_type_id === typeId);
    };

    return (
        <div className="edit-user-popup-overlay" onClick={handleOverlayClick}>
            <div className={`edit-user-popup${isClosing ? ' closing' : ''}`}>
                <div className="popup-settings-container">
                    <h2 className="popup-title">עריכת משתמש</h2>                    
                    <div className="input-group">
                        <label>שם פרטי</label>
                        <input
                            type="text"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                        />
                    </div>

                    <div className="input-group">
                        <label>שם משפחה</label>
                        <input
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                        />
                    </div>

                    <div className="input-group">
                        <label>טלפון</label>
                        <input
                            type="text"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)} // Update phone number
                        />
                    </div>

                    <div className="input-group">
                        <label>תפקיד</label>
                        <select value={role} onChange={(e) => setRole(e.target.value)}>
                            <option value="מנהל מערכת">מנהל מערכת</option>
                            <option value="מתפעל">מתפעל</option>
                            <option value="מומחה">מומחה</option>
                            <option value="לקוח">לקוח</option>
                        </select>
                    </div>

                    <div className="checkbox-row">
                        <div className="input-group checkbox-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={isOperator}
                                    onChange={(e) => setIsOperator(e.target.checked)}
                                />
                                משתמש תפעול
                            </label>
                        </div>
                        <div className="input-group checkbox-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={receiveVoice}
                                    onChange={(e) => setReceiveVoice(e.target.checked)}
                                />
                                מקבל הודעות קוליות
                            </label>
                        </div>
                    </div>
                    {/* Notification Settings Section */}
                    <div className="notification-settings">
                        <div className="notification-title">הגדרת הודעות</div>
                        <div className="notification-row">
                            <label className="notification-label">
                                הודעות על פעולות לא צפויות בסכומים מעל:
                                <input type="checkbox" checked={notifyNonExpected} onChange={e => setNotifyNonExpected(e.target.checked)} />
                            </label>
                            <div className="notification-input-group">
                                <div className="input-currency-wrapper">
                                    <input
                                        type="number"
                                        className="notification-input"
                                        value={notAdjustedMinTotal}
                                        onChange={e => setNotAdjustedMinTotal(Number(e.target.value))}
                                    />
                                    <span className="currency-inside">₪</span>
                                </div>
                            </div>
                        </div>
                        <div className="notification-row">
                            <label className="notification-label">
                                הודעות על פעולות נגררות בסכומים מעל:
                                <input type="checkbox" checked={notifyNegative} onChange={e => setNotifyNegative(e.target.checked)} />
                            </label>
                            <div className="input-currency-wrapper">
                                <input
                                    type="number"
                                    className="notification-input"
                                    value={nigrarotMinTotal}
                                    onChange={e => setNigrarotMinTotal(Number(e.target.value))}
                                />
                                <span className="currency-inside">₪</span>
                            </div>
                        </div>
                        <div className="notification-row">
                            <label className="notification-label-no-ckbx">
                                לאחר:
                            </label>
                            <div className="input-currency-wrapper" style={{width: '120px'}}>
                                <input
                                    type="number"
                                    className="notification-input"
                                    value={nigrarotMinDays}
                                    onChange={e => setNigrarotMinDays(Number(e.target.value))}
                                />
                                <span className="currency-inside">ימים</span>
                            </div>
                        </div>
                        <div className="input-group-checkbox-group">
                            <label>
                                <input type="checkbox" checked={notifyBudget} onChange={e => setNotifyBudget(e.target.checked)} />
                                הודעות על חריגות בתקציב
                            </label>
                        </div>
                        <div className="input-group-checkbox-group">
                            <label>
                                <input type="checkbox" checked={notifyIncomePace} onChange={e => setNotifyIncomePace(e.target.checked)} />
                                הודעה על קצב נמוך בהכנסות
                            </label>
                        </div>
                        <div className="input-group-checkbox-group">
                            <label>
                                <input type="checkbox" checked={notifyExpensePace} onChange={e => setNotifyExpensePace(e.target.checked)} />
                                הודעה על קצב גבוה בהוצאות
                            </label>
                        </div>
                    </div>

                    {error && <div className="error-message">{error}</div>} {/* Display any error message */}
                </div>
                <div className="popup-actions">
                    <button className="cancel-btn" onClick={handleClose}>ביטול</button>
                    <button
                        className="confirm-btn"
                        onClick={handleSubmit}
                        disabled={isLoading} // Disable button when loading
                    >
                        {isLoading ? "מעדכן..." : "אישור"} {/* Show loading text while processing */}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditUserPopup;
